import { useState } from "react";
import { useRestoreVersionData } from "../../../../api/versioningService";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { useInvalidateVersionCaches } from "../../mapping-version/hooks/useInvalidateVersionCaches";
import { VERSION_HISTORY_COPY } from "../constants/versionHistory.constants";
import { VersionHistoryGroup } from "../types/versionHistory.types";
import { resolveVersionLabel } from "../utils/versionHistory.utils";

/**
 * Activate reuses the existing restore endpoint (decision 6) rather than a new one --
 * POST /mapping-versions/restore/:versionId, already used by the mapping-version tooling.
 */
export function useVersionActivation(params: { onActivated?: (restoreResult: unknown) => void; refetchVersions: () => void }) {
  const [versionToActivate, setVersionToActivate] = useState<VersionHistoryGroup | null>(null);
  const { mutate, isLoading } = useRestoreVersionData();
  const invalidateVersionCaches = useInvalidateVersionCaches();

  const requestActivate = (version: VersionHistoryGroup) => setVersionToActivate(version);
  const cancelActivate = () => setVersionToActivate(null);

  const confirmActivate = () => {
    if (!versionToActivate?.versionId) return;

    const label = resolveVersionLabel(versionToActivate);
    mutate(
      { versionId: versionToActivate.versionId },
      {
        onSuccess: data => {
          successMessage(VERSION_HISTORY_COPY.activateSuccess(label));
          setVersionToActivate(null);
          // Activating replaces the mapping's default version, so MappingHeaderCard (on the
          // drawing tool screen, not this panel) is reading a stale one until this runs -- see
          // useInvalidateVersionCaches. `refetchVersions` covers this panel's own list; it is
          // partly redundant with the groupedHistory invalidation below, kept as-is since it is
          // also relied on by rename/reporting-year edits (useVersionRename,
          // useVersionDetailsEditor) and changing its contract is out of scope here.
          invalidateVersionCaches(versionToActivate.drawingToolId ?? undefined);
          params.refetchVersions();
          params.onActivated?.(data.result);
        },
        // No local toast here: the axios interceptor already toasts the server's message (or a
        // fallback) for every failed request, so adding one here would show the error twice.
        onError: () => {
          setVersionToActivate(null);
        }
      }
    );
  };

  return { versionToActivate, isActivating: isLoading, requestActivate, cancelActivate, confirmActivate };
}
